import { FormattedMessage } from 'react-intl';
import { ConfigStepBody } from './config-step/config-step';
import { PurchaseOrderConfigStepFooter } from './config-step/create-config-step';
import { fieldsTranslations, messages } from './messages';
import { actionMap, initialState } from './reducer';
import type { PurchaseOrderImportDrawerProps } from './types';
import { ImportType } from '../../../../../components/csv-import/data/types/import-type';
import { StepType } from '../../../../../components/csv-import/data/types/step-type';
import { ImportProvider } from '../../../../../components/csv-import/ui/components/import-context';
import { SingleImportDrawer } from '../../../../../components/csv-import/ui/components/single-import-drawer/single-import-drawer';
import { CreateMappingStepBody } from '../../../../../components/csv-import/ui/components/steps/create-mapping-step/create-mapping-step';
import { CreateMappingStepFooter } from '../../../../../components/csv-import/ui/components/steps/create-mapping-step/create-mapping-step-footer';
import {
  ImportIsProcessingStepBody,
  ImportIsProcessingStepFooter,
} from '../../../../../components/csv-import/ui/components/steps/import-is-processing-step/import-is-processing-step';
import { getTranslationFn } from '../../../../../components/csv-import/ui/utils';
import { CSV_PURCHASE_ORDER_IMPORT_JOB_COMPLETE } from '../../../../../services/pusher-service/pusher-contract-messages';
import { Workflows } from '../../../../../tracking/tracking-constants';
import { WorkflowSteps } from '../../../../../tracking/use-workflow-tracking';

const stepsConfig = {
  [StepType.CREATE_CONFIG]: {
    id: StepType.CREATE_CONFIG,
    title: <FormattedMessage {...messages.createConfigStep} />,
    body: <ConfigStepBody />,
    footer: <PurchaseOrderConfigStepFooter />,
  },
  [StepType.CREATE_MAPPING]: {
    id: StepType.CREATE_MAPPING,
    title: <FormattedMessage {...messages.createMappingStep} />,
    body: <CreateMappingStepBody />,
    footer: <CreateMappingStepFooter />,
  },
  [StepType.IMPORT_IS_PROCESSING]: {
    id: StepType.IMPORT_IS_PROCESSING,
    title: <FormattedMessage {...messages.importResult} />,
    body: (
      <ImportIsProcessingStepBody
        detailedSuccessMessage={messages.importDetailsSuccess}
        successMessage={<FormattedMessage {...messages.importResultSuccess} />}
        title={<FormattedMessage {...messages.importResult} />}
      />
    ),
    footer: <ImportIsProcessingStepFooter />,
  },
};

const steps = [stepsConfig[StepType.CREATE_CONFIG], stepsConfig[StepType.CREATE_MAPPING], stepsConfig[StepType.IMPORT_IS_PROCESSING]];

const translateField = getTranslationFn(fieldsTranslations, ImportType.purchaseOrder);

const createCustomImportSettings = (form: { inventoryWarehouse?: string }) => {
  return {
    inventoryWarehouseId: form.inventoryWarehouse,
  };
};

export const PurchaseOrderImportDrawer = ({ onClose, trackProcess }: PurchaseOrderImportDrawerProps) => {
  const onCancel = (step: string) => {
    return () => {
      onClose();
      trackProcess(WorkflowSteps.CANCEL, { step });
    };
  };

  return (
    <ImportProvider
      actionMap={actionMap}
      createCustomImportSettings={createCustomImportSettings}
      importType={ImportType.purchaseOrder}
      initialState={initialState}
      onClose={onCancel(stepsConfig[StepType.IMPORT_IS_PROCESSING].id)}
      pusherEvent={CSV_PURCHASE_ORDER_IMPORT_JOB_COMPLETE}
      trackProcess={trackProcess}
      translateField={translateField}
      workflowType={Workflows.CSV_PURCHASE_ORDER_IMPORT_FLOW}
    >
      <SingleImportDrawer header={<FormattedMessage {...messages.modalDrawer} />} onCancel={onCancel} steps={steps} />
    </ImportProvider>
  );
};
